import { datasets, totalCells } from "../data";
import { LanguageText } from "./LanguageText";

const guideSections = [
  ["start", "从这里开始", "Start here"],
  ["find-data", "查找研究", "Find studies"],
  ["find-genes", "检索基因", "Search genes"],
  ["read-atlas", "阅读图谱", "Read the atlas"],
  ["verify", "核对结果", "Verify results"],
  ["contribute", "提交与引用", "Submit & cite"],
] as const;

export function GuideContent() {
  return (
    <div className="guide-layout">
      <aside className="guide-nav">
        <div className="guide-nav-heading">
          <strong>TRYPCELL GUIDE</strong>
          <LanguageText
            as="small"
            zh="按科研任务快速导航"
            en="Navigate by research task"
          />
        </div>
        <nav aria-label="Guide sections">
          {guideSections.map(([id, zh, en], index) => (
            <a href={`#${id}`} key={id}>
              <small>{String(index + 1).padStart(2, "0")}</small>
              <LanguageText zh={zh} en={en} />
            </a>
          ))}
        </nav>
      </aside>

      <div className="guide-content">
        <section id="start">
          <p className="section-number">01 · START WITH A QUESTION</p>
          <LanguageText
            as="h2"
            zh="先判断你要找的是哪一类信息"
            en="First decide what kind of information you need"
          />
          <LanguageText
            as="p"
            zh={`TrypCell 当前目录包含 ${datasets.length} 项经过来源核验的锥虫研究，共 ${totalCells.toLocaleString()} 个目录细胞条目。对初次使用者，最有效的入口不是浏览所有页面，而是先从下面四种科研问题中选择一种。`}
            en={`TrypCell currently catalogues ${datasets.length} source-verified Trypanosoma studies and ${totalCells.toLocaleString()} cell entries. New users should begin with one of the four research questions below rather than browsing every page.`}
          />

          <div className="guide-goal-grid">
            <article>
              <span>STUDY</span>
              <LanguageText as="h3" zh="我想找一项研究或数据集" en="I need a study or data set" />
              <LanguageText
                as="p"
                zh="按物种、阶段、测序技术、作者或仓库登录号检索。"
                en="Search by species, stage, technology, author or repository accession."
              />
              <a href="/datasets/">
                <LanguageText zh="打开数据目录 →" en="Open the data catalogue →" />
              </a>
            </article>
            <article>
              <span>CELLS</span>
              <LanguageText as="h3" zh="我想查看细胞分群或生命周期" en="I need cells, clusters or lifecycle stages" />
              <LanguageText
                as="p"
                zh="进入作者坐标图谱，查看细胞、样本、阶段和处理条件。"
                en="Open author-released coordinates and inspect cells, samples, stages and conditions."
              />
              <a href="/explore/">
                <LanguageText zh="打开交互图谱 →" en="Open the interactive atlas →" />
              </a>
            </article>
            <article>
              <span>GENE</span>
              <LanguageText as="h3" zh="我想知道某个基因在哪些研究中可查" en="I need to find where a gene is queryable" />
              <LanguageText
                as="p"
                zh="按作者特征编号、基因名称或产物搜索真实表达层。"
                en="Search real expression layers by author feature ID, gene name or product."
              />
              <a href="/genes/">
                <LanguageText zh="打开跨研究基因检索 →" en="Open cross-study gene search →" />
              </a>
            </article>
            <article>
              <span>HOST RESPONSE</span>
              <LanguageText as="h3" zh="我想找宿主对克氏锥虫的反应" en="I need host responses to T. cruzi" />
              <LanguageText
                as="p"
                zh="宿主分子变化与锥虫细胞图谱是不同数据类型，应进入专门的宿主反应证据入口。"
                en="Host molecular changes and parasite-cell atlases are different data types; use the dedicated host-response evidence entry."
              />
              <a href="/explore/?species=tc&tab=host">
                <LanguageText zh="查看宿主反应证据 →" en="Open host-response evidence →" />
              </a>
              <a
                className="guide-secondary-link"
                href="https://chagasdb.tagc.univ-amu.fr/"
                target="_blank"
                rel="noreferrer"
              >
                ChagasDB ↗
              </a>
            </article>
          </div>

          <div className="guide-note">
            <LanguageText
              as="p"
              zh="本站的核心范围是直接测量锥虫细胞的单细胞研究。若问题是人、鼠或其他宿主在感染后的分子变化，请使用宿主反应入口，不要把宿主基因结果解释成锥虫基因表达。"
              en="The portal’s core scope is single-cell research that directly measures Trypanosoma cells. For molecular changes in infected human, mouse or other hosts, use the host-response entry and do not interpret host-gene results as parasite-gene expression."
            />
          </div>
        </section>

        <section id="find-data">
          <p className="section-number">02 · FIND A STUDY</p>
          <LanguageText
            as="h2"
            zh="第一次检索，从“数据”页面开始"
            en="For a first search, begin on the Data page"
          />
          <LanguageText
            as="p"
            zh="ChagasDB 的指南采用“搜索—筛选—查看字段—下载”的任务顺序。TrypCell 保留这个清楚的思路，但先要求用户选择研究，因为不同物种、株系、平台、参考版本和表达单位不能默认直接比较。"
            en="The ChagasDB guide follows a search–filter–inspect–download sequence. TrypCell keeps that clear task order but asks users to choose a study first, because species, strains, platforms, references and expression units are not automatically comparable."
          />
          <ol className="guide-steps">
            <li>
              <span>1</span>
              <LanguageText
                zh="打开“数据”，先选择 T. b. brucei 或 T. cruzi。"
                en="Open Data and first choose T. b. brucei or T. cruzi."
              />
            </li>
            <li>
              <span>2</span>
              <LanguageText
                zh="在搜索框输入作者、生命周期阶段、技术或仓库登录号。"
                en="Enter an author, lifecycle stage, technology or repository accession."
              />
            </li>
            <li>
              <span>3</span>
              <LanguageText
                zh="打开匹配记录的“科研数据护照”，核对细胞数口径、表达单位、坐标和参考版本。"
                en="Open the matching record’s Research data passport and check cell-count scope, expression unit, coordinates and reference."
              />
            </li>
            <li>
              <span>4</span>
              <LanguageText
                zh="再选择论文、原始仓库、本站图谱或专业表达图谱。"
                en="Then choose the paper, source repository, TrypCell atlas or professional expression atlas."
              />
            </li>
          </ol>

          <div className="guide-search-examples">
            <article>
              <code>PRJEB41744</code>
              <LanguageText
                as="p"
                zh="按 ENA 登录号定位 Briggs 等人的研究。"
                en="Locate the Briggs study by its ENA accession."
              />
            </article>
            <article>
              <code>Smart-seq2</code>
              <LanguageText
                as="p"
                zh="查找使用相应板式单细胞方案的数据。"
                en="Find data produced with the corresponding plate-based workflow."
              />
            </article>
            <article>
              <code>amastigote</code>
              <LanguageText
                as="p"
                zh="查找描述无鞭毛体阶段的研究记录。"
                en="Find records describing the amastigote stage."
              />
            </article>
            <article>
              <code>T. cruzi</code>
              <LanguageText
                as="p"
                zh="使用物种按钮筛选克氏锥虫研究。"
                en="Use the species control to filter T. cruzi studies."
              />
            </article>
          </div>

          <a className="button button-dark guide-primary-action" href="/datasets/">
            <LanguageText zh="开始查找研究" en="Start finding studies" />
          </a>
        </section>

        <section id="find-genes">
          <p className="section-number">03 · SEARCH A GENE</p>
          <LanguageText
            as="h2"
            zh="先找作者特征编号，再打开表达图"
            en="Find the author feature ID before opening expression"
          />
          <LanguageText
            as="p"
            zh="基因页可按作者矩阵中的特征编号、基因名称或产物注释搜索，并列出该特征在哪些研究的真实表达层中可查询。搜索结果表示“可在哪里查看”，不表示这些研究已经被联合分析。"
            en="The Genes page searches author-matrix feature IDs, gene names and product annotations, then lists the real expression layers in which a feature is queryable. A result means “available here,” not that the studies have been jointly analysed."
          />
          <div className="guide-card-grid">
            <article>
              <strong>Tb927.6.4280</strong>
              <LanguageText
                as="p"
                zh="精确的作者特征编号通常是最可靠的检索词。"
                en="An exact author feature ID is usually the most reliable query."
              />
            </article>
            <article>
              <strong>amastin / GAPDH</strong>
              <LanguageText
                as="p"
                zh="名称或产物搜索适合发现候选项，但打开结果后仍需核对编号。"
                en="Name or product searches help find candidates, but the identifier must still be checked."
              />
            </article>
          </div>
          <ul className="guide-checklist">
            <li>
              <LanguageText
                zh="核对作者编号、当前映射编号和参考版本。"
                en="Check the author ID, any current mapped ID and the reference version."
              />
            </li>
            <li>
              <LanguageText
                zh="核对表达单位：原始计数、归一化值和缩放值不是同一尺度。"
                en="Check the unit: raw counts, normalized values and scaled values are not the same scale."
              />
            </li>
            <li>
              <LanguageText
                zh="若页面明确显示表达层不可用，本站不会生成替代值。"
                en="When an expression layer is marked unavailable, the portal does not generate substitute values."
              />
            </li>
            <li>
              <LanguageText
                zh="不要在不同物种之间根据相似名称自动认定同源基因。"
                en="Do not infer orthology across species from similar names alone."
              />
            </li>
          </ul>
          <a className="button button-dark guide-primary-action" href="/genes/">
            <LanguageText zh="开始检索基因" en="Start searching genes" />
          </a>
        </section>

        <section id="read-atlas">
          <p className="section-number">04 · READ THE ATLAS</p>
          <LanguageText
            as="h2"
            zh="把图谱当作数据地图，而不是细胞照片"
            en="Read the atlas as a data map, not a cell photograph"
          />
          <div className="guide-card-grid">
            <article>
              <strong>Hover · 悬停</strong>
              <LanguageText
                as="p"
                zh="临时查看细胞编号和可公开的作者元数据。"
                en="Preview the cell ID and public author metadata."
              />
            </article>
            <article>
              <strong>Point · 单点</strong>
              <LanguageText
                as="p"
                zh="固定一个细胞，详情显示在图外侧栏，不遮挡选点。"
                en="Pin one cell; details appear outside the plot so points remain selectable."
              />
            </article>
            <article>
              <strong>Box / lasso · 框选 / 套索</strong>
              <LanguageText
                as="p"
                zh="选择多个细胞并查看组成或下载元数据，不会重新聚类。"
                en="Select cells for composition or metadata download; this does not recluster them."
              />
            </article>
            <article>
              <strong>Colour · 颜色</strong>
              <LanguageText
                as="p"
                zh="分类色表示阶段、样本、处理或 cluster；连续色阶可表示当前基因值。"
                en="Categories encode stages, samples, conditions or clusters; continuous scales may encode a gene value."
              />
            </article>
          </div>

          <dl className="guide-definitions">
            <div>
              <dt>Smart-seq2</dt>
              <LanguageText
                as="dd"
                zh="板式单细胞 RNA 测序方案，可获得全长 cDNA；具体建库和定量必须回到论文方法核对。"
                en="A plate-based single-cell RNA-seq workflow that can generate full-length cDNA; library preparation and quantification must be checked in the paper."
              />
            </div>
            <div>
              <dt>Chromium 10x</dt>
              <LanguageText
                as="dd"
                zh="液滴微流控单细胞平台，常用细胞条形码和 UMI；3′/5′化学版本及过滤流程影响可比性。"
                en="A droplet-microfluidics single-cell platform commonly using cell barcodes and UMIs; 3′/5′ chemistry and filtering affect comparability."
              />
            </div>
            <div>
              <dt>PCA</dt>
              <LanguageText
                as="dd"
                zh="线性降维结果；PC 轴表示表达变化方向，不是细胞在组织中的物理位置。"
                en="A linear reduction; PC axes describe directions of expression variation, not physical positions in tissue."
              />
            </div>
            <div>
              <dt>UMAP</dt>
              <LanguageText
                as="dd"
                zh="把高维邻近关系投影到二维或三维。相邻点通常更相似，但远距离和轴没有直接生物学单位。"
                en="Projects high-dimensional neighbourhoods into two or three dimensions. Nearby points are often more similar, but long distances and axes have no direct biological unit."
              />
            </div>
            <div>
              <dt>Cluster</dt>
              <LanguageText
                as="dd"
                zh="算法在所选表达空间中得到的细胞组；没有标记基因和独立证据时，不能自动称为细胞类型或生命周期阶段。"
                en="An algorithmic group in a chosen expression space; without marker and independent evidence it is not automatically a cell type or lifecycle stage."
              />
            </div>
          </dl>

          <div className="guide-source-links">
            <a
              href="https://www.nature.com/articles/nprot.2014.006"
              target="_blank"
              rel="noreferrer"
            >
              <strong>Smart-seq2 protocol</strong>
              <LanguageText zh="原始方法论文 ↗" en="Original method paper ↗" />
            </a>
            <a
              href="https://www.10xgenomics.com/support/universal-three-prime-gene-expression/documentation/steps/experimental-design-and-planning/getting-started-single-cell-3-gene-expression"
              target="_blank"
              rel="noreferrer"
            >
              <strong>10x Genomics</strong>
              <LanguageText zh="官方技术说明 ↗" en="Official technical guidance ↗" />
            </a>
            <a
              href="https://joss.theoj.org/papers/10.21105/joss.00861"
              target="_blank"
              rel="noreferrer"
            >
              <strong>UMAP</strong>
              <LanguageText zh="原始软件论文 ↗" en="Original software paper ↗" />
            </a>
          </div>
        </section>

        <section id="verify">
          <p className="section-number">05 · VERIFY BEFORE USE</p>
          <LanguageText
            as="h2"
            zh="把结果用于论文前，核对这七项"
            en="Check these seven items before using a result"
          />
          <ul className="guide-checklist guide-verification-list">
            <li><LanguageText zh="物种和亚种是否正确？" en="Is the species or subspecies correct?" /></li>
            <li><LanguageText zh="株系、生命周期阶段、样本和处理条件是否符合问题？" en="Do strain, lifecycle stage, sample and condition fit the question?" /></li>
            <li><LanguageText zh="细胞数采用作者过滤前还是过滤后口径？" en="Does the cell count refer to pre- or post-filter author data?" /></li>
            <li><LanguageText zh="测序技术、表达单位和参考版本是什么？" en="What are the technology, expression unit and reference version?" /></li>
            <li><LanguageText zh="坐标、矩阵和注释来自作者还是本站转换？" en="Are coordinates, matrix and annotations author-released or portal-converted?" /></li>
            <li><LanguageText zh="论文、仓库登录号和文件来源能否打开？" en="Can the paper, accession and file source be opened?" /></li>
            <li><LanguageText zh="数据许可是否允许你的下载或再发布用途？" en="Does the data licence permit the intended download or redistribution?" /></li>
          </ul>

          <div className="guide-layer-grid">
            <article>
              <span>AUTHOR-PROCESSED</span>
              <LanguageText as="p" zh="作者发布的矩阵、坐标、元数据和注释。" en="Author-released matrices, coordinates, metadata and annotations." />
            </article>
            <article>
              <span>PORTAL-CONVERTED</span>
              <LanguageText as="p" zh="本站只转换文件或字段格式，并保留源文件和校验记录。" en="Portal file or field conversion with source and integrity records retained." />
            </article>
            <article>
              <span>PORTAL-REANALYSED</span>
              <LanguageText as="p" zh="只有流程、代码、参数和参考均可复核时才使用；不会冒充作者结果。" en="Used only for reproducible workflows with code, parameters and references; never presented as author results." />
            </article>
          </div>

          <div className="guide-note">
            <LanguageText
              as="p"
              zh="四个常见误读：UMAP 远距离不是生物学距离；cluster 不自动等于细胞类型；灰色或缺失值不自动等于零表达；不同研究的数值不能因颜色相似就直接比较。"
              en="Four common errors: long UMAP distances are not biological distances; a cluster is not automatically a cell type; grey or missing is not automatically zero expression; similar colours do not make values from different studies directly comparable."
            />
          </div>
        </section>

        <section id="contribute">
          <p className="section-number">06 · SUBMIT AND CITE</p>
          <LanguageText
            as="h2"
            zh="提交研究、更正记录并正确引用"
            en="Submit studies, correct records and cite correctly"
          />
          <LanguageText
            as="p"
            zh="提交无需账户，但必须提供可核验的论文或明确标注的预印本、公共仓库登录号、物种、株系、测序方法、参考版本、表达层和许可。大型矩阵与原始测序文件应存放在 GEO、SRA、ENA、BioStudies、Zenodo 等持久仓库，而不是上传到本站表单。"
            en="Submission requires no account, but must include a verifiable paper or clearly labelled preprint, public accession, species, strain, sequencing method, reference, expression layer and licence. Large matrices and raw reads belong in persistent repositories such as GEO, SRA, ENA, BioStudies or Zenodo, not in the portal form."
          />

          <div className="guide-review-flow" aria-label="Submission review flow">
            {[
              ["01", "Received", "已收到"],
              ["02", "Source check", "来源检查"],
              ["03", "Scientific review", "科学审核"],
              ["04", "Rights review", "权利审核"],
              ["05", "Versioned release", "版本化发布"],
            ].map(([number, en, zh]) => (
              <div key={number}>
                <span>{number}</span>
                <strong>{en}</strong>
                <small>{zh}</small>
              </div>
            ))}
          </div>

          <LanguageText
            as="p"
            zh="提交不等于发布。人工审核通过后，记录才可能进入版本化发布。用于生物学分析时，应引用原始论文或预印本以及公共仓库登录号；若使用本站转换、术语映射或审计结果，再记录 TrypCell 数据集页面、版本和访问日期。"
            en="Submission is not publication. A record can enter a versioned release only after human review. Biological analyses should cite the original paper or preprint and public accession; when portal conversions, terminology mappings or audits informed the work, also record the TrypCell data page, version and access date."
          />

          <div className="guide-source-links">
            <a href="/submit/">
              <strong>TrypCell Submit</strong>
              <LanguageText zh="提交研究或更正 →" en="Submit a study or correction →" />
            </a>
            <a href="/standards/">
              <strong>Data passports</strong>
              <LanguageText zh="核对引用与许可规则 →" en="Check citation and rights rules →" />
            </a>
            <a
              href="https://www.ncbi.nlm.nih.gov/geo/info/seq.html"
              target="_blank"
              rel="noreferrer"
            >
              <strong>NCBI GEO</strong>
              <LanguageText zh="测序数据提交说明 ↗" en="Sequencing-data submission guidance ↗" />
            </a>
            <a
              href="https://ena-docs.readthedocs.io/en/latest/submit/general-guide/metadata.html"
              target="_blank"
              rel="noreferrer"
            >
              <strong>ENA</strong>
              <LanguageText zh="官方元数据模型 ↗" en="Official metadata model ↗" />
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
